import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Link, TextField, Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import AuthLayout from '../components/AuthLayout';
import OAuthProviderButtons from '../components/OAuthProviderButtons';
import OtpInput from '../components/OtpInput';
import { useSignUpViewModel } from '../viewmodels/AuthViewModels';
import { useSession } from '../viewmodels/SessionViewModel';

const TERMS_URL = 'https://orkaapps.com/links/terms';
const PRIVACY_URL = 'https://orkaapps.com/links/privacy';
const PASSWORD_RULES = '8+ characters with upper and lower case letters and a number';

function AuthTermsLine() {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center', lineHeight: 1.6 }}>
      By continuing, you agree to our
      {' '}
      <Link href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Service</Link>
      {' '}
      and
      {' '}
      <Link href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
      .
    </Typography>
  );
}

export default function SignUpView() {
  const vm = useSignUpViewModel();
  const { setSignedIn } = useSession();
  const [form, setForm] = useState({
    firstName: '', lastName: '', mobile: '', email: '', password: '',
  });
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = 'Create account · OrkaCrew';
  }, []);

  const field = (name) => ({
    value: form[name],
    onChange: (e) => setForm((f) => ({ ...f, [name]: e.target.value })),
    fullWidth: true,
    margin: 'dense',
  });

  const submit = (e) => {
    e.preventDefault();
    if (vm.step === 'details') vm.requestCode(form);
    else vm.createAccount(form, code);
  };

  return (
    <AuthLayout
      title="Create account"
      subtitle="One Orka account works across all Orka apps."
      footer={(
        <>
          Already have an account?
          {' '}
          <Link component={RouterLink} to="/signin">Sign in</Link>
        </>
      )}
    >
      {vm.error && <Alert severity="error" sx={{ mb: 2 }}>{vm.error}</Alert>}
      <Box component="form" onSubmit={submit} noValidate>
        {vm.step === 'details' ? (
          <>
            <TextField label="Name" autoFocus helperText="Shown on your OrkaCrew profile" {...field('firstName')} />
            <TextField label="Email" type="email" autoComplete="email" {...field('email')} />
            <TextField
              label="Mobile"
              helperText="Digits only, with country code if needed"
              inputProps={{ inputMode: 'numeric' }}
              {...field('mobile')}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              helperText={PASSWORD_RULES}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...field('password')}
            />
          </>
        ) : (
          <>
            <Typography color="text.secondary" align="center">
              We sent a 4 digit code to
              {' '}
              <b>{form.mobile}</b>
            </Typography>
            <OtpInput value={code} onChange={setCode} disabled={vm.busy} />
          </>
        )}
        <Button type="submit" variant="contained" fullWidth size="large" disabled={vm.busy} sx={{ mt: 2 }}>
          {vm.busy
            ? <CircularProgress size={22} color="inherit" />
            : (vm.step === 'details' ? 'Create account' : 'Verify and create account')}
        </Button>
        {vm.step === 'verify' && (
          <Button fullWidth sx={{ mt: 1 }} onClick={() => { vm.setStep('details'); setCode(''); }}>Change details</Button>
        )}
      </Box>
      {vm.step === 'details' && (
        <OAuthProviderButtons
          onAuthenticated={setSignedIn}
          onError={vm.reportError}
        />
      )}
      <AuthTermsLine />
    </AuthLayout>
  );
}
